/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { useResizeObserver } from 'src/hooks/useResizeObserver/useResizeObserver';
import { ColorOverrides, Colors, Responsive, Sizes } from 'src/design-system/style-types';

import styles from './Loading.style';

export interface DefaultProps {
  className: string;
  fullWidth: boolean;
  size: Sizes;
  color: Responsive<Colors | ColorOverrides>;
  top?: Sizes;
  bottom?: Sizes;
  absoluteCenter?: boolean;
  // pair with absoluteCenter when the loader is positioned against the
  // viewport (full-page loaders): shifts the horizontal center past the
  // fixed spine, which overlays the content area's inline-start edge.
  // Loaders anchored to a positioned container (cards) must not set this
  adjustForSpine?: boolean;
  height: string;
}

const defaultProps: DefaultProps = {
  className: '',
  fullWidth: true,
  size: '2xl',
  color: 'accent',
  height: 'auto',
};

const Loading = (props: DefaultProps) => {
  const { className, absoluteCenter, adjustForSpine } = props;
  // the spine is position:fixed and overlaps the content area on its
  // inline-start side, so an absolutely-centered loader would otherwise
  // center against the whole viewport and sit half a spine-width out of
  // place; measure the spine (as BulkActionBar does) and shift the center
  // by half its width. Lookup happens in a layout effect declared before
  // useResizeObserver so the element is present when the observer attaches
  const spineRef = useRef<HTMLElement | null>(null);
  const [spineWidth, setSpineWidth] = useState(0);

  const onResizeSpine = useCallback(() => {
    setSpineWidth(spineRef.current?.offsetWidth ?? 0);
  }, []);

  useLayoutEffect(() => {
    if (!absoluteCenter || !adjustForSpine) {
      return;
    }
    spineRef.current = document.getElementById('dashboard-spine');
    onResizeSpine();
  }, [absoluteCenter, adjustForSpine, onResizeSpine]);

  useResizeObserver({ elRef: spineRef, onResizeCallback: onResizeSpine });

  return (
    <div
      role="alert"
      aria-label="loading"
      data-qa="loadingSpinner"
      className={className}
      css={styles.container(props, spineWidth)}
    >
      <div css={styles.loading(props)}>
        {[1, 2, 3, 4].map((num) => (
          <div key={num} />
        ))}
      </div>
    </div>
  );
};

Loading.defaultProps = defaultProps;

export default Loading;
